import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../auth/firebase";

const ROOT_COLLECTION = "attendanceTimesheets";
const WEEK_NUMBERS = Array.from({ length: 10 }, (_, index) => index + 1);

export const HOURS_PER_WEEK = 20;

function safeId(value, fallback = "unknown") {
  const cleaned = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return cleaned || fallback;
}

function weekKey(weekNumber) {
  return `week${Number(weekNumber)}`;
}

function normaliseWeekRecord(record = {}) {
  const attendanceHours =
    record.attendanceHours === "" || record.attendanceHours == null
      ? ""
      : Number(record.attendanceHours);

  const leaveApplied = Boolean(record.leaveApplied);

  const leaveHours =
    leaveApplied && record.leaveHours !== "" && record.leaveHours != null
      ? Number(record.leaveHours)
      : "";
  const savedDailyReminders =
    record.dailyReminders ||
    record.dailyAttendanceReminders ||
    {};

  return {
    attendanceHours:
      Number.isFinite(attendanceHours)
        ? attendanceHours
        : "",

    leaveApplied,

    leaveHours:
      Number.isFinite(leaveHours)
        ? leaveHours
        : "",

    dailyReminders: {
      day1: Boolean(
        savedDailyReminders.day1
      ),
      day2: Boolean(
        savedDailyReminders.day2
      ),
      day3: Boolean(
        savedDailyReminders.day3
      ),
      day4: Boolean(
        savedDailyReminders.day4
      ),
      day5: Boolean(
        savedDailyReminders.day5
      ),
    },

    weeklyReminderSent:
      Boolean(
        record.weeklyReminderSent ??
        record.weeklyAttendanceReminderSent
      ),

    studentResponse: String(
      record.studentResponse ??
      record.responseFromStudent ??
      ""
    ),
  };
}

export function createEmptyWeeks() {
  return Object.fromEntries(
    WEEK_NUMBERS.map((number) => [
      weekKey(number),
      normaliseWeekRecord(),
    ])
  );
}

export function normaliseWeeks(weeks = {}) {
  return Object.fromEntries(
    WEEK_NUMBERS.map((number) => {
      const key = weekKey(number);
      return [key, normaliseWeekRecord(weeks[key])];
    })
  );
}

/**
 * Returns true when the week has attendance hours or approved leave.
 *
 * Blank future weeks are excluded from the percentage calculation.
 */
export function weekHasEntry(record = {}) {
  const hasAttendance =
    record.attendanceHours !== "" &&
    record.attendanceHours != null &&
    Number.isFinite(Number(record.attendanceHours));

  return hasAttendance || Boolean(record.leaveApplied);
}

/**
 * Matches the Excel logic:
 *
 * Numeric value -> use attendance hours.
 * L             -> use 20 credited hours.
 * L-8           -> use 8 credited hours.
 *
 * In this application:
 * - leaveApplied = true with blank leaveHours is treated as L.
 * - leaveApplied = true with leaveHours = 8 is treated as L-8.
 * - approved leave overrides attendanceHours for that week.
 */
export function getCreditedWeekHours(record = {}) {
  if (!weekHasEntry(record)) {
    return 0;
  }

  if (record.leaveApplied) {
    const hasSpecificLeaveHours =
      record.leaveHours !== "" &&
      record.leaveHours != null &&
      Number.isFinite(Number(record.leaveHours));

    if (!hasSpecificLeaveHours) {
      return HOURS_PER_WEEK;
    }

    return Math.min(
      Math.max(Number(record.leaveHours), 0),
      HOURS_PER_WEEK
    );
  }

  return Math.min(
    Math.max(Number(record.attendanceHours || 0), 0),
    HOURS_PER_WEEK
  );
}
const STANDARD_HOURS_PER_WEEK = 20;
const VOE_HOURS_PER_WEEK = 32;

function getStudentHoursPerWeek(student = {}) {
  const enrolmentType = String(
    student.enrolmentType || ""
  )
    .trim()
    .toUpperCase();

  const attendanceDaysPerWeek = Number(
    student.attendanceDaysPerWeek
  );

  return attendanceDaysPerWeek === 5
    ? VOE_HOURS_PER_WEEK
    : STANDARD_HOURS_PER_WEEK;
}

function getStudentCreditedWeekHours(
  record = {},
  hoursPerWeek
) {
  if (record.leaveApplied) {
    const leaveHours = record.leaveHours;

    // Blank approved leave means full weekly credit.
    if (
      leaveHours === "" ||
      leaveHours == null
    ) {
      return hoursPerWeek;
    }

    const parsedLeaveHours =
      Number(leaveHours);

    return Number.isFinite(parsedLeaveHours)
      ? Math.min(
          Math.max(parsedLeaveHours, 0),
          hoursPerWeek
        )
      : 0;
  }

  const parsedAttendanceHours =
    Number(record.attendanceHours);

  return Number.isFinite(
    parsedAttendanceHours
  )
    ? Math.min(
        Math.max(parsedAttendanceHours, 0),
        hoursPerWeek
      )
    : 0;
}

/**
 * Calculates totals for one student across Week 1 to Week 10.
 *
 * possibleHours uses only weeks where attendance or leave has been entered.
 * This prevents future blank weeks from lowering the current percentage.
 */
export function calculateStudentAttendanceSummary(
  student = {}
) {
  const weeks = normaliseWeeks(
    student.weeks
  );

  const hoursPerWeek =
    getStudentHoursPerWeek(student);

  let enteredWeeks = 0;
  let totalAttendanceHours = 0;
  let totalLeaveHours = 0;
  let totalCreditedHours = 0;
  let runningCreditedHours = 0;

  const weekPercentages = {};
  const upToDatePercentages = {};

  WEEK_NUMBERS.forEach((weekNumber) => {
    const key = weekKey(weekNumber);
    const record = weeks[key];
    const hasEntry = weekHasEntry(record);

    if (!hasEntry) {
      weekPercentages[key] = null;

      if (weekNumber >= 2) {
        upToDatePercentages[
          `afterWeek${weekNumber}`
        ] = null;
      }

      return;
    }

    enteredWeeks += 1;

    const creditedHours =
      getStudentCreditedWeekHours(
        record,
        hoursPerWeek
      );

    if (record.leaveApplied) {
      totalLeaveHours += creditedHours;
    } else {
      totalAttendanceHours +=
        creditedHours;
    }

    totalCreditedHours += creditedHours;
    runningCreditedHours += creditedHours;

    weekPercentages[key] = Math.round(
      (creditedHours / hoursPerWeek) * 100
    );

    if (weekNumber >= 2) {
      upToDatePercentages[
        `afterWeek${weekNumber}`
      ] = Math.round(
        (
          runningCreditedHours /
          (weekNumber * hoursPerWeek)
        ) * 100
      );
    }
  });

  const possibleHours =
    enteredWeeks * hoursPerWeek;

  const attendancePercentage =
    possibleHours > 0
      ? Math.round(
          (
            totalCreditedHours /
            possibleHours
          ) * 100
        )
      : 0;

  return {
    hoursPerWeek,
    enteredWeeks,
    possibleHours,
    totalAttendanceHours,
    totalLeaveHours,
    totalCreditedHours,
    attendancePercentage,
    weekPercentages,
    upToDatePercentages,
  };
}

function normaliseStudent(student = {}) {
  return {
    ...student,

    studentId: String(
      student.studentId || ""
    ).trim(),

    studentName: String(
      student.studentName || ""
    ).trim(),

    attendanceDaysPerWeek:
      Number(
        student.attendanceDaysPerWeek
      ) === 5
        ? 5
        : 3,

    weeks: normaliseWeeks(
      student.weeks
    ),
  };
}

function buildStudentsObject(students = []) {
  return Object.fromEntries(
    students
      .filter((student) => String(student.studentId || "").trim())
      .map((student) => {
        const normalised = normaliseStudent(student);

        return [
          safeId(normalised.studentId, "student-unknown"),
          normalised,
        ];
      })
  );
}

export function buildTimesheetId({
  campus,
  courseCode,
  courseName,
  year,
  term,
}) {
  return [
    safeId(campus, "campus"),
    safeId(courseCode || courseName, "course"),
    safeId(year, "year"),
    safeId(term, "term"),
  ].join("_");
}

export function getTimesheetRef(metadata) {
  const timesheetId = buildTimesheetId(metadata);
  return doc(db, ROOT_COLLECTION, timesheetId);
}

function normaliseTimesheetSnapshot(snapshot) {
  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();

  const students = Object.fromEntries(
    Object.entries(data.students || {}).map(([key, student]) => [
      key,
      normaliseStudent(student),
    ])
  );

  return {
    id: snapshot.id,
    firestorePath: snapshot.ref.path,
    ...data,
    students,
  };
}

/**
 * Loads one complete class timesheet.
 *
 * Firestore:
 * attendanceTimesheets/{campus_course_year_term}
 */
export async function getAttendanceTimesheet(metadata) {
  const snapshot = await getDoc(getTimesheetRef(metadata));
  return normaliseTimesheetSnapshot(snapshot);
}

/**
 * Returns saved attendance only for student IDs currently visible
 * in the selected class roster.
 */
export async function getClassAttendance({ metadata, studentIds }) {
  const timesheet = await getAttendanceTimesheet(metadata);

  if (!timesheet) {
    return [];
  }

  const requestedIds = new Set(
    (studentIds || []).map((studentId) =>
      String(studentId || "").trim()
    )
  );

  return Object.values(timesheet.students || {})
    .filter((student) =>
      requestedIds.has(String(student.studentId || "").trim())
    )
    .map((student) => ({
      ...student,
      recordStatus: timesheet.recordStatus || "draft",
    }));
}

/**
 * Creates or updates one complete class timesheet.
 *
 * The deterministic document ID means saving again updates the same
 * Course + Campus + Year + Term document instead of creating duplicates.
 */
export async function saveClassAttendance({ metadata, students }) {
  if (!metadata?.campus) {
    throw new Error("Campus is required.");
  }

  if (!metadata?.courseName) {
    throw new Error("Course is required.");
  }

  if (!metadata?.year) {
    throw new Error("Year is required.");
  }

  if (!metadata?.term) {
    throw new Error("Term is required.");
  }

  const timesheetRef = getTimesheetRef(metadata);
  const studentsObject = buildStudentsObject(students);

  await runTransaction(db, async (transaction) => {
    const existingSnapshot = await transaction.get(timesheetRef);

    const existingData = existingSnapshot.exists()
      ? existingSnapshot.data()
      : {};

    const timestamp = serverTimestamp();
    const submitted = metadata.recordStatus === "submitted";

    transaction.set(timesheetRef, {
      timesheetId: timesheetRef.id,
      campus: metadata.campus,
      courseCode: metadata.courseCode || "",
      courseName: metadata.courseName,
      year: Number(metadata.year),
      term: metadata.term,
      termStartDate: metadata.termStartDate || "",
      termEndDate: metadata.termEndDate || "",
      recordStatus: metadata.recordStatus || "draft",
      studentCount: Object.keys(studentsObject).length,
      students: studentsObject,
      createdAt: existingData.createdAt || timestamp,
      updatedAt: timestamp,
      submittedAt: submitted
        ? timestamp
        : existingData.submittedAt || null,
    });
  });

  return timesheetRef.id;
}

export const saveOrUpdateClassTimesheet = saveClassAttendance;

/**
 * Returns every saved timesheet from the root collection.
 */
export async function listAttendanceTimesheets() {
  const snapshot = await getDocs(
    collection(db, ROOT_COLLECTION)
  );

  return snapshot.docs
    .map(normaliseTimesheetSnapshot)
    .filter(Boolean)
    .sort((a, b) => {
      const yearDifference =
        Number(b.year || 0) - Number(a.year || 0);

      if (yearDifference !== 0) {
        return yearDifference;
      }

      const courseDifference = String(
        a.courseName || ""
      ).localeCompare(String(b.courseName || ""));

      if (courseDifference !== 0) {
        return courseDifference;
      }

      return String(a.term || "").localeCompare(
        String(b.term || "")
      );
    });
}
/*
 * Add these Firestore functions to AttendanceRecordService.js.
 *
 * Make sure its existing firebase/firestore import includes:
 *   doc, getDoc, serverTimestamp, setDoc
 *
 * These functions use the existing `db` instance already used by
 * AttendanceRecordService.js.
 */

const ATTENDANCE_TERM_DATES_COLLECTION =
  "attendanceTermDates";

function normaliseTermDateDocumentPart(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function attendanceTermDateDocumentId({
  campus,
  year,
  term,
}) {
  return [
    campus,
    year,
    term,
  ]
    .map(normaliseTermDateDocumentPart)
    .join("__");
}

export async function getAttendanceTermDates({
  campus,
  year,
  term,
}) {
  if (!campus || !year || !term) {
    return null;
  }

  const documentReference = doc(
    db,
    ATTENDANCE_TERM_DATES_COLLECTION,
    attendanceTermDateDocumentId({
      campus,
      year,
      term,
    })
  );

  const snapshot =
    await getDoc(documentReference);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  };
}

export async function saveAttendanceTermDates({
  campus,
  year,
  term,
  termStartDate,
  termEndDate,
}) {
  if (
    !campus ||
    !year ||
    !term ||
    !termStartDate ||
    !termEndDate
  ) {
    throw new Error(
      "Campus, year, term, start date and end date are required."
    );
  }

  const documentReference = doc(
    db,
    ATTENDANCE_TERM_DATES_COLLECTION,
    attendanceTermDateDocumentId({
      campus,
      year,
      term,
    })
  );

  const payload = {
    campus: String(campus).trim(),
    year: Number(year),
    term: String(term).trim(),
    termStartDate:
      String(termStartDate).trim(),
    termEndDate:
      String(termEndDate).trim(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(
    documentReference,
    payload,
    {
      merge: true,
    }
  );

  return payload;
}
/**
 * Finds class timesheets matching Course -> Year -> Term.
 *
 * Campus is optional so Find/View can display matching records
 * from both campuses.
 */
export async function getAllTimesheets({
  courseCode,
  courseName,
  year,
  term,
  campus,
}) {
  const allTimesheets = await listAttendanceTimesheets();

  const wantedCode = String(courseCode || "")
    .trim()
    .toLowerCase();

  const wantedName = String(courseName || "")
    .trim()
    .toLowerCase();

  const wantedCampus = String(campus || "")
    .trim()
    .toLowerCase();

  return allTimesheets.filter((timesheet) => {
    const codeMatches = wantedCode
      ? String(timesheet.courseCode || "")
          .trim()
          .toLowerCase() === wantedCode
      : true;

    const nameMatches =
      !wantedCode && wantedName
        ? String(timesheet.courseName || "")
            .trim()
            .toLowerCase() === wantedName
        : true;

    const yearMatches =
      Number(timesheet.year) === Number(year);

    const termMatches =
      String(timesheet.term || "") === String(term || "");

    const campusMatches = wantedCampus
      ? String(timesheet.campus || "")
          .trim()
          .toLowerCase() === wantedCampus
      : true;

    return (
      codeMatches &&
      nameMatches &&
      yearMatches &&
      termMatches &&
      campusMatches
    );
  });
}
export async function updateAttendanceStudentFollowUp({
  timesheetId,
  studentKey,
  weekNumber,
  attendanceDaysPerWeek,
  dailyReminders,
  weeklyReminderSent,
  studentResponse,
}) {
  const parsedWeekNumber =
    Number(weekNumber);

  if (!timesheetId) {
    throw new Error(
      "Attendance timesheet ID is required."
    );
  }

  if (!studentKey) {
    throw new Error(
      "Student record key is required."
    );
  }

  if (
    !Number.isInteger(parsedWeekNumber) ||
    parsedWeekNumber < 1 ||
    parsedWeekNumber > 10
  ) {
    throw new Error(
      "A valid week number is required."
    );
  }

  const timesheetRef = doc(
    db,
    ROOT_COLLECTION,
    String(timesheetId)
  );

  const cleanedDailyReminders = {
    day1: Boolean(
      dailyReminders?.day1
    ),
    day2: Boolean(
      dailyReminders?.day2
    ),
    day3: Boolean(
      dailyReminders?.day3
    ),
    day4: Boolean(
      dailyReminders?.day4
    ),
    day5: Boolean(
      dailyReminders?.day5
    ),
  };

  const cleanedAttendanceDays =
    Number(attendanceDaysPerWeek) === 5
      ? 5
      : 3;

  await runTransaction(
    db,
    async (transaction) => {
      const snapshot =
        await transaction.get(
          timesheetRef
        );

      if (!snapshot.exists()) {
        throw new Error(
          "Attendance timesheet could not be found."
        );
      }

      const timesheetData =
        snapshot.data() || {};

      const students = {
        ...(timesheetData.students || {}),
      };

      const existingStudent =
        students[studentKey];

      if (!existingStudent) {
        throw new Error(
          "Student attendance record could not be found."
        );
      }

      const weekName =
        `week${parsedWeekNumber}`;

      const weeks = {
        ...(existingStudent.weeks || {}),
      };

      weeks[weekName] = {
        ...(weeks[weekName] || {}),

        dailyReminders:
          cleanedDailyReminders,

        weeklyReminderSent:
          Boolean(
            weeklyReminderSent
          ),

        studentResponse: String(
          studentResponse || ""
        ).trim(),
      };

      students[studentKey] = {
        ...existingStudent,

        attendanceDaysPerWeek:
          cleanedAttendanceDays,

        weeks,
      };

      transaction.update(
        timesheetRef,
        {
          students,
          updatedAt:
            serverTimestamp(),
        }
      );
    }
  );
}